import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { useIsPlatformAdmin } from "@/hooks/usePlatformSummary";
import { CompanySelectorModal } from "@/components/CompanySelectorModal";

interface ProtectedRouteProps {
  children: React.ReactNode;
  portalGuard?: "controller" | "worker" | "contract_labor" | "customer";
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, portalGuard }) => {
  const { user, loading } = useAuth();
  const { isPersonnel, isVendor, isContractLabor, isCustomer, userProfile, isLoading: permLoading } = usePermissions();
  const { needsSelection, isLoading: companyLoading } = useCompanySelector();
  const { data: isPlatformAdmin, isLoading: platformLoading } = useIsPlatformAdmin();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  if ((portalGuard && permLoading) || companyLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // User has no profile — either a platform admin or an orphaned account
  if (!userProfile && !permLoading) {
    // Still checking platform admin status — show spinner briefly
    if (platformLoading) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      );
    }
    // Confirmed platform admin — redirect to /platform
    if (isPlatformAdmin) {
      return <Navigate to="/platform" replace />;
    }
    // No profile AND not a platform admin — redirect to auth
    return <Navigate to="/auth" replace />;
  }

  // Show company picker if user belongs to multiple companies and hasn't chosen yet
  if (needsSelection) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <CompanySelectorModal open={true} onSelected={() => window.location.reload()} />
      </div>
    );
  }

  // Controller portal: block all portal-only roles
  if (portalGuard === "controller" && (isPersonnel || isVendor || isContractLabor || isCustomer)) {
    if (isCustomer) return <Navigate to="/portal/customer" replace />;
    if (isContractLabor) return <Navigate to="/portal/contract-labor" replace />;
    return <Navigate to="/portal/worker" replace />;
  }

  // Worker portal: personnel only
  if (portalGuard === "worker" && !isPersonnel) {
    return <Navigate to="/controller" replace />;
  }

  // Contract labor portal: contract_labor only
  if (portalGuard === "contract_labor" && !isContractLabor) {
    return <Navigate to="/controller" replace />;
  }

  // Customer portal: customer roles only
  if (portalGuard === "customer" && !isCustomer) {
    return <Navigate to="/controller" replace />;
  }

  return <>{children}</>;
};
